import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { 
  FiBox, FiZap, FiBarChart2, FiShield, 
  FiTruck, FiAward, FiMail, FiLock, 
  FiLoader, FiAlertCircle, FiCheckCircle,
  FiArrowRight, FiLayers, FiMenu, FiX,
  FiCpu, FiDatabase, FiFileText, FiTrendingUp,
  FiLock as FiSecure, FiClock, FiStar,
  FiPieChart, FiDownload, FiMessageSquare
} from 'react-icons/fi';
import supabase from '../supabase/supabase';

// Custom Canvas Component for Fluent Mouse Particles
const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    const numParticles = 120; // Number of floating particles

    const resize = () => {
      if (!canvas.parentElement) return;
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    };
    
    window.addEventListener('resize', resize);
    resize();

    // Track mouse globally so it works even if hovering over other elements
    const handleMouseMove = (e) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };
    
    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseLeave);

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.8;
        this.vy = (Math.random() - 0.5) * 0.8;
        this.radius = Math.random() * 1.5 + 0.5;
        this.baseVx = this.vx;
        this.baseVy = this.vy;
      }
      update() {
        // Distance to mouse
        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Fluently follow the mouse if it gets close
        if (dist < 250) {
           const force = (250 - dist) / 250;
           this.vx += (dx / dist) * force * 0.04;
           this.vy += (dy / dist) * force * 0.04;
        } else {
           // Smoothly return to base velocity
           this.vx += (this.baseVx - this.vx) * 0.02;
           this.vy += (this.baseVy - this.vy) * 0.02;
        }
        
        // Speed limit (friction)
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed > 2.5) {
            this.vx = (this.vx / speed) * 2.5;
            this.vy = (this.vy / speed) * 2.5;
        }

        this.x += this.vx;
        this.y += this.vy;

        // Bounce off edges smoothly
        if (this.x < 0 || this.x > canvas.width) {
            this.vx *= -1;
            this.baseVx *= -1;
            this.x = Math.max(0, Math.min(canvas.width, this.x));
        }
        if (this.y < 0 || this.y > canvas.height) {
            this.vy *= -1;
            this.baseVy *= -1;
            this.y = Math.max(0, Math.min(canvas.height, this.y));
        }
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.fill();
      }
    }

    for (let i = 0; i < numParticles; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 1. Draw the fluent ambient light glowing behind particles
      if (mouseRef.current.x > -100 && mouseRef.current.y > -100) {
        const gradient = ctx.createRadialGradient(
          mouseRef.current.x, mouseRef.current.y, 0, 
          mouseRef.current.x, mouseRef.current.y, 400
        );
        gradient.addColorStop(0, 'rgba(74, 222, 128, 0.15)'); // Radiant green glow
        gradient.addColorStop(1, 'rgba(74, 222, 128, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // 2. Draw dynamic connecting lines to mouse cursor
      particles.forEach(p => {
         const dx = mouseRef.current.x - p.x;
         const dy = mouseRef.current.y - p.y;
         const dist = Math.sqrt(dx * dx + dy * dy);
         
         if (dist < 180) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * (1 - dist/180)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
         }
      });

      // 3. Update and draw particles
      particles.forEach(p => {
         p.update();
         p.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 z-0 pointer-events-none"
    />
  );
};

export const Landing = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('insurer');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) throw signInError;
      // On success, AuthContext will automatically re-render and route to Dashboard
    } catch (err) {
      console.error('Login Error:', err);
      setError('Invalid credentials');
      setTimeout(() => setError(null), 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-alt font-sans text-gray-800 overflow-x-hidden">
      
      {/* Sticky Top Navigation with Integrated Login */}
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 backdrop-blur-md py-5 border-b border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
          
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center shadow-lg shadow-brand/20">
              <SafeIcon icon={FiBox} className="text-white text-2xl" />
            </div>
            <span className="text-2xl font-black text-gray-900 tracking-tight">SalvageOS</span>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex gap-8 text-sm font-bold text-gray-600">
            <a href="#features" className="hover:text-brand transition-colors">Platform</a>
            <a href="#workflow" className="hover:text-brand transition-colors">Workflow</a>
            <a href="#reports" className="hover:text-brand transition-colors">Reports</a>
            <a href="#leaderboard" className="hover:text-brand transition-colors">Performance</a>
          </div>

          {/* Desktop Inline Login Portal */}
          <div className="hidden lg:flex items-center gap-4">
            <form onSubmit={handleLogin} className="flex items-center gap-2 relative">
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-48 pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all"
                />
              </div>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input 
                  type="password" 
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-40 pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all"
                />
              </div>
              <button 
                type="submit" 
                disabled={loading}
                className="px-5 py-2 bg-brand text-white rounded-lg text-sm font-bold hover:bg-brand-light transition-all shadow-md shadow-brand/20 disabled:opacity-70 flex items-center justify-center min-w-[80px]"
              >
                {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Log In'}
              </button>

              {/* Inline Error Tooltip */}
              <AnimatePresence>
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full mt-2 right-0 bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg flex items-center gap-2 whitespace-nowrap"
                  >
                    <SafeIcon icon={FiAlertCircle} size={12} />
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
            <div className="w-px h-8 bg-gray-200 mx-2"></div>
            <button onClick={() => navigate('/auth')} className="text-sm font-bold text-gray-500 hover:text-brand transition-colors whitespace-nowrap">
              Partner Portal
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="lg:hidden p-2 text-gray-600 bg-gray-100 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} size={24} />
          </button>
        </div>

        {/* Mobile Dropdown Portal */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="px-6 py-6 space-y-6">
                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4">Access Portal</h3>
                  <form onSubmit={handleLogin} className="space-y-3">
                    <input 
                      type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                    <input 
                      type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                    {error && <p className="text-red-500 text-xs font-bold">{error}</p>}
                    <button type="submit" disabled={loading} className="w-full py-3 bg-brand text-white rounded-xl text-sm font-bold hover:bg-brand-light">
                      {loading ? 'Authenticating...' : 'Secure Login'}
                    </button>
                  </form>
                </div>
                <div className="flex flex-col gap-4">
                  <a href="#features" onClick={() => setMobileMenuOpen(false)} className="font-bold text-gray-700">Platform Features</a>
                  <a href="#workflow" onClick={() => setMobileMenuOpen(false)} className="font-bold text-gray-700">How it Works</a>
                  <a href="#reports" onClick={() => setMobileMenuOpen(false)} className="font-bold text-gray-700">Reports</a>
                  <a href="#leaderboard" onClick={() => setMobileMenuOpen(false)} className="font-bold text-gray-700">Performance</a>
                  <button onClick={() => navigate('/auth')} className="text-brand font-bold text-left">Partner Portal</button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section with Moving Gradient Background & Interactive Particles */}
      <motion.div 
        animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        style={{
          backgroundSize: '200% 200%',
          backgroundImage: 'linear-gradient(-45deg, #0f3e23 0%, #1a5d3a 25%, #0a2e18 50%, #258251 75%, #0f3e23 100%)'
        }}
        className="relative pt-40 pb-24 lg:pt-52 lg:pb-36 overflow-hidden min-h-[90vh] flex items-center"
      >
        {/* Interactive Fluent Particle Field */}
        <ParticleBackground />

        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          {/* Subtle Animated Background Orbs */}
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, -100, 0],
              y: [0, 50, 0],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-[30%] -right-[10%] w-[800px] h-[800px] rounded-full bg-brand-light/10 blur-[120px]"
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.3, 1],
              x: [0, 100, 0],
              y: [0, -50, 0],
            }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-green-400/5 blur-[100px]"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 pointer-events-none">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white pointer-events-auto"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-bold text-brand-accent uppercase tracking-widest mb-6 shadow-sm backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                The Operating System for Salvage
              </div>
              <h1 className="text-5xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight drop-shadow-sm">
                Automate your <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-green-300">
                  Salvage Lifecycle
                </span>
              </h1>
              <p className="text-lg text-brand-accent/90 mb-10 max-w-lg leading-relaxed font-medium drop-shadow-sm">
                Connect insurers with a top-rated network of salvagers. Automate distribution, manage exceptions, and track payout yields—all in one intelligent platform.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <a href="#features" className="w-full sm:w-auto px-8 py-4 bg-white text-brand-dark rounded-xl font-black text-lg hover:bg-gray-100 transition-colors shadow-xl text-center">
                  Explore Platform
                </a>
                <button onClick={() => navigate('/auth')} className="w-full sm:w-auto px-8 py-4 bg-white/10 border border-white/30 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-colors text-center backdrop-blur-sm">
                  Partner Portal
                </button>
              </div>

              <div className="mt-12 flex items-center gap-4 text-sm text-brand-accent/80 font-medium">
                <div className="flex -space-x-2">
                  <img className="w-8 h-8 rounded-full border-2 border-brand-dark" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" />
                  <img className="w-8 h-8 rounded-full border-2 border-brand-dark" src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" />
                  <img className="w-8 h-8 rounded-full border-2 border-brand-dark" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" />
                </div>
                <p>Trusted by 50+ leading processors</p>
              </div>
            </motion.div>

            {/* Right Content - Abstract UI Mockup */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block pointer-events-auto"
            >
              {/* Synthetic App UI */}
              <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden border border-white/20 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="mx-auto bg-white border border-gray-200 rounded-md px-24 py-1 text-[10px] text-gray-400 font-mono">
                    app.salvageos.com
                  </div>
                </div>
                <div className="p-6 bg-gray-50/80 flex gap-6">
                  {/* Fake Sidebar */}
                  <div className="w-16 flex flex-col gap-4">
                    <div className="w-8 h-8 rounded-lg bg-brand/10 mx-auto"></div>
                    <div className="w-8 h-8 rounded-lg bg-gray-200 mx-auto"></div>
                    <div className="w-8 h-8 rounded-lg bg-gray-200 mx-auto"></div>
                  </div>
                  {/* Fake Content */}
                  <div className="flex-1 space-y-4">
                    <div className="flex justify-between items-end">
                      <div>
                        <div className="w-24 h-4 bg-gray-300 rounded mb-2"></div>
                        <div className="w-48 h-8 bg-gray-800 rounded"></div>
                      </div>
                      <div className="w-32 h-8 bg-brand rounded-lg"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="h-20 bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="w-12 h-3 bg-gray-200 rounded mb-3"></div>
                        <div className="w-20 h-6 bg-brand-light rounded"></div>
                      </div>
                      <div className="h-20 bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="w-12 h-3 bg-gray-200 rounded mb-3"></div>
                        <div className="w-20 h-6 bg-green-500 rounded"></div>
                      </div>
                      <div className="h-20 bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="w-12 h-3 bg-gray-200 rounded mb-3"></div>
                        <div className="w-20 h-6 bg-orange-500 rounded"></div>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-12 bg-white border border-gray-100 rounded-lg shadow-sm flex items-center px-4 justify-between">
                          <div className="flex gap-4 items-center">
                            <div className="w-6 h-6 rounded-full bg-gray-100"></div>
                            <div className="w-24 h-3 bg-gray-800 rounded"></div>
                          </div>
                          <div className="w-16 h-4 bg-green-100 rounded-full"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-8 top-16 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-4"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                  <SafeIcon icon={FiCheckCircle} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase">Offer Accepted</p>
                  <p className="font-black text-gray-900">R 12,500.00</p>
                </div>
              </motion.div>

              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -left-12 bottom-16 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-4"
              >
                <div className="w-10 h-10 bg-brand/10 rounded-full flex items-center justify-center text-brand">
                  <SafeIcon icon={FiCpu} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase">AI Routing</p>
                  <p className="font-black text-gray-900">Assigned to TechRecycle</p>
                </div>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </motion.div>

      {/* Value Proposition Section */}
      <section className="py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight tracking-tight">
                Complete control over your <span className="text-brand">salvage lifecycle.</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Stop guessing and start optimizing. SalvageOS gives you the tools to turn a messy, manual process into a streamlined revenue center with full transparency from write-off to final payout.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-4">
                  <div className="w-12 h-12 rounded-full border-4 border-white bg-green-100 flex items-center justify-center z-30 shadow-sm"><SafeIcon icon={FiCheckCircle} className="text-green-600" /></div>
                  <div className="w-12 h-12 rounded-full border-4 border-white bg-blue-100 flex items-center justify-center z-20 shadow-sm"><SafeIcon icon={FiTrendingUp} className="text-blue-600" /></div>
                  <div className="w-12 h-12 rounded-full border-4 border-white bg-purple-100 flex items-center justify-center z-10 shadow-sm"><SafeIcon icon={FiClock} className="text-purple-600" /></div>
                </div>
                <p className="text-sm font-bold text-gray-500">Trusted by industry leaders</p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-surface-alt rounded-[2rem] p-8 lg:p-10 border border-gray-100 shadow-xl shadow-brand/5 relative overflow-hidden"
            >
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-brand/5 rounded-full blur-2xl"></div>
              <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3 relative z-10">
                <div className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center shadow-md">
                  <SafeIcon icon={FiZap} />
                </div>
                With SalvageOS, you will:
              </h3>
              <ul className="space-y-5 relative z-10">
                {[
                  "Know exactly where every salvage item is.",
                  "Measure supplier performance.",
                  "Reduce manual work.",
                  "Produce reports in seconds.",
                  "Never chase spreadsheets again."
                ].map((item, i) => (
                  <motion.li 
                    key={i} 
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + (i * 0.1) }}
                    className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="text-green-600" size={14} />
                    </div>
                    <span className="text-gray-800 font-bold">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comprehensive Features Grid */}
      <section id="features" className="py-24 bg-surface-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-sm font-black text-brand uppercase tracking-widest mb-3">Core Platform</h2>
            <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">Everything you need to automate your salvage operations.</h3>
            <p className="text-gray-600 text-lg">We replaced messy spreadsheets and disjointed email chains with a unified, secure platform built specifically for the insurance salvage ecosystem.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FiDatabase,
                title: "Bulk Import Engine",
                desc: "Upload Excel or CSV files containing hundreds of claims. Our parser automatically cleans, standardizes, and syncs them to the database."
              },
              {
                icon: FiCpu,
                title: "AI-Powered Routing",
                desc: "Stop manually matching items. Our intelligent algorithm reads the claim details and instantly assigns it to the most relevant, highest-performing buyer."
              },
              {
                icon: FiShield,
                title: "Exception Management",
                desc: "Dedicated queues for handling buyer rejections, missing information, and disputes securely without losing the audit trail."
              },
              {
                icon: FiFileText,
                title: "Automated Financials",
                desc: "Track every offer, generate PDF statements, and reconcile payments instantly. Know exactly what is outstanding and what is paid."
              },
              {
                icon: FiTrendingUp,
                title: "Live Analytics",
                desc: "Monitor recovery yields, average turnaround times, and departmental performance through interactive, real-time dashboards."
              },
              {
                icon: FiAward,
                title: "Partner Leaderboards",
                desc: "Drive network performance by tracking buyer win-rates, payout totals, and courier speeds to ensure you always get the best value."
              }
            ].map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-brand/30 transition-all group"
              >
                <div className="w-14 h-14 rounded-2xl bg-brand/5 text-brand flex items-center justify-center text-2xl mb-6 group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                  <SafeIcon icon={feature.icon} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Workflow Section */}
      <section id="workflow" className="py-24 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-sm font-black text-brand uppercase tracking-widest mb-3">How it Works</h2>
            <h3 className="text-3xl lg:text-4xl font-black text-gray-900">A seamless loop from write-off to recovery.</h3>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 items-center">
            {/* Tabs / Steps */}
            <div className="w-full lg:w-1/2 space-y-4">
              {[
                { id: 'insurer', title: '1. Insurer Uploads Data', desc: 'Claims handlers bulk upload BER (Beyond Economic Repair) inventory via Excel or manual entry.' },
                { id: 'ai', title: '2. System Distributes', desc: 'SalvageOS analyzes the make, model, and damage to route the claim to the ideal recycling partner.' },
                { id: 'salvager', title: '3. Salvager Accepts & Pays', desc: 'The buyer receives an instant alert, accepts the claim, arranges pickup and sets their offer price. As the AI learns, it would know the average price for items and give suggested prices.' },
                { id: 'report', title: '4. Reconciliation', desc: 'Insurer managers view consolidated financial statements and track total recovered value in real-time.' }
              ].map((step) => (
                <div 
                  key={step.id}
                  onClick={() => setActiveTab(step.id)}
                  className={`p-6 rounded-2xl cursor-pointer transition-all border-2 ${activeTab === step.id ? 'border-brand bg-brand/5' : 'border-transparent hover:bg-gray-50'}`}
                >
                  <h4 className={`text-xl font-bold mb-2 ${activeTab === step.id ? 'text-brand-dark' : 'text-gray-900'}`}>{step.title}</h4>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>

            {/* Dynamic Image/Visual based on Tab */}
            <div className="w-full lg:w-1/2">
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-gray-100 bg-gray-50 aspect-square lg:aspect-auto lg:h-[500px] relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 flex items-center justify-center p-8"
                  >
                    {activeTab === 'insurer' && (
                      <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                        <div className="h-4 w-1/2 bg-gray-200 rounded mb-6"></div>
                        <div className="space-y-3">
                          <div className="h-10 bg-brand/10 border border-brand/20 border-dashed rounded-lg flex items-center justify-center text-brand font-bold text-sm">
                            <SafeIcon icon={FiFileText} className="mr-2" /> claims_export.xlsx
                          </div>
                          <div className="h-10 bg-brand text-white rounded-lg flex items-center justify-center font-bold text-sm shadow-md">
                            Import 245 Claims
                          </div>
                        </div>
                      </div>
                    )}
                    {activeTab === 'ai' && (
                      <div className="relative w-full max-w-sm h-64">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-brand text-white rounded-full flex items-center justify-center z-10 shadow-xl shadow-brand/30">
                          <SafeIcon icon={FiCpu} size={32} />
                        </div>
                        <div className="absolute top-0 left-0 w-16 h-16 bg-white border border-gray-200 rounded-xl shadow-sm"></div>
                        <div className="absolute bottom-0 right-0 w-16 h-16 bg-white border border-gray-200 rounded-xl shadow-sm"></div>
                        <div className="absolute top-0 right-10 w-16 h-16 bg-white border border-gray-200 rounded-xl shadow-sm"></div>
                        {/* Connecting lines conceptually */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none stroke-brand/20" strokeWidth="2" fill="none">
                          <path d="M 32 32 L 192 128" strokeDasharray="4 4" />
                          <path d="M 352 32 L 192 128" strokeDasharray="4 4" />
                          <path d="M 352 224 L 192 128" strokeDasharray="4 4" />
                        </svg>
                      </div>
                    )}
                    {activeTab === 'salvager' && (
                      <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center"><SafeIcon icon={FiLayers} /></div>
                          <div>
                            <p className="font-bold text-gray-900">New Claim Assigned</p>
                            <p className="text-xs text-gray-500">Apple iPhone 14 Pro</p>
                          </div>
                        </div>
                        <div className="h-10 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center justify-center font-bold text-sm">
                          Make Offer: R 4,500
                        </div>
                      </div>
                    )}
                    {activeTab === 'report' && (
                      <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                        <div className="flex justify-between items-end mb-6">
                          <div className="h-4 w-1/3 bg-gray-200 rounded"></div>
                          <div className="h-6 w-1/3 bg-green-100 rounded"></div>
                        </div>
                        <div className="flex items-end gap-2 h-32 pt-4 border-b border-gray-100">
                          <div className="flex-1 bg-brand/20 rounded-t-md h-1/3"></div>
                          <div className="flex-1 bg-brand/40 rounded-t-md h-1/2"></div>
                          <div className="flex-1 bg-brand/60 rounded-t-md h-3/4"></div>
                          <div className="flex-1 bg-brand rounded-t-md h-full"></div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Dashboard & Reports Section */}
      <section id="reports" className="py-24 bg-surface-alt border-y border-gray-100 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-sm font-black text-brand uppercase tracking-widest mb-3 flex items-center justify-center gap-2">
              <SafeIcon icon={FiPieChart} /> Executive Dashboard
            </h2>
            <h3 className="text-3xl lg:text-5xl font-black text-gray-900 mb-6 tracking-tight">Data-driven salvage decisions.</h3>
            <p className="text-gray-600 text-lg">Generate reports in seconds. Get real-time visibility into recovered value, turnaround times, and departmental performance without chasing a single spreadsheet.</p>
          </div>

          {/* Dashboard UI Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-[2rem] shadow-2xl border border-gray-200 overflow-hidden"
          >
            {/* Mock Top Bar */}
            <div className="bg-gray-50 border-b border-gray-100 px-6 py-5 flex justify-between items-center">
              <div className="font-bold text-gray-800 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand text-white flex items-center justify-center shadow-sm">
                  <SafeIcon icon={FiPieChart} /> 
                </div>
                Management Reports
              </div>
              <div className="flex gap-2">
                <span className="hidden sm:inline-block bg-white border border-gray-200 text-gray-500 text-xs font-bold px-4 py-2 rounded-xl shadow-sm">Last 30 Days</span>
                <span className="bg-gray-900 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-2 shadow-md cursor-pointer hover:bg-black transition-colors">
                  <SafeIcon icon={FiDownload} /> Export PDF
                </span>
              </div>
            </div>
            
            <div className="p-6 lg:p-10 bg-white">
              {/* KPI Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <div className="bg-surface-alt p-6 rounded-2xl border border-gray-100 shadow-sm border-l-4 border-l-brand relative overflow-hidden group">
                  <div className="absolute -right-4 -top-4 w-16 h-16 bg-brand/5 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 relative z-10">Recovered Value</p>
                  <h4 className="text-3xl font-black text-gray-900 relative z-10">R 2.4M</h4>
                </div>
                <div className="bg-surface-alt p-6 rounded-2xl border border-gray-100 shadow-sm border-l-4 border-l-blue-500 relative overflow-hidden group">
                  <div className="absolute -right-4 -top-4 w-16 h-16 bg-blue-500/5 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 relative z-10">Recovery %</p>
                  <h4 className="text-3xl font-black text-gray-900 relative z-10">84.2%</h4>
                </div>
                <div className="bg-surface-alt p-6 rounded-2xl border border-gray-100 shadow-sm border-l-4 border-l-purple-500 relative overflow-hidden group">
                  <div className="absolute -right-4 -top-4 w-16 h-16 bg-purple-500/5 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 relative z-10">Avg Turnaround</p>
                  <h4 className="text-3xl font-black text-gray-900 relative z-10">3.5<span className="text-lg text-gray-400 ml-1">Days</span></h4>
                </div>
                <div className="bg-surface-alt p-6 rounded-2xl border border-gray-100 shadow-sm border-l-4 border-l-orange-500 relative overflow-hidden group">
                  <div className="absolute -right-4 -top-4 w-16 h-16 bg-orange-500/5 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 relative z-10">Outstanding Coll.</p>
                  <h4 className="text-3xl font-black text-gray-900 relative z-10">18</h4>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Value by Department Chart */}
                <div className="lg:col-span-2">
                  <h5 className="font-bold text-gray-900 mb-6 text-lg">Value by Department</h5>
                  <div className="space-y-6">
                    {[
                      { name: 'Mobile Phones', value: 85, amount: 'R 1.2M', color: 'bg-brand' },
                      { name: 'Computers & IT', value: 65, amount: 'R 850k', color: 'bg-blue-500' },
                      { name: 'Household Appliances', value: 45, amount: 'R 250k', color: 'bg-purple-500' },
                      { name: 'Furniture', value: 20, amount: 'R 100k', color: 'bg-orange-500' },
                    ].map((dept, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-sm font-bold text-gray-700 mb-2">
                          <span>{dept.name}</span>
                          <span className="text-gray-900">{dept.amount}</span>
                        </div>
                        <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div 
                            initial={{ width: 0 }}
                            whileInView={{ width: `${dept.value}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, delay: i * 0.15, ease: "easeOut" }}
                            className={`h-full ${dept.color}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Feedback Views / Performance */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h5 className="font-bold text-gray-900 mb-6 text-lg">Supplier Performance</h5>
                  <div className="space-y-5">
                    <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-black text-sm">98%</div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">Supplier Satisfaction</p>
                        <p className="text-xs text-gray-500">Based on recent collections</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-black text-lg">
                        <SafeIcon icon={FiTruck} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">On-Time Pickups</p>
                        <p className="text-xs text-gray-500">92% within SLA bounds</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="w-12 h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-black text-lg">
                        <SafeIcon icon={FiMessageSquare} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">Dispute Rate</p>
                        <p className="text-xs text-gray-500">&lt; 2% overall exception rate</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leaderboard & Gamification Section */}
      <section id="leaderboard" className="py-24 bg-brand-dark relative overflow-hidden">
        {/* Background Decor */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand/50 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-yellow-500/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-white space-y-8"
            >
              <div>
                <h2 className="text-sm font-black text-yellow-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiAward} /> Network Gamification
                </h2>
                <h3 className="text-3xl lg:text-5xl font-black text-white mb-6 leading-tight">
                  Drive competition. <br />
                  <span className="text-brand-accent">Maximize payouts.</span>
                </h3>
                <p className="text-lg text-brand-accent/80 leading-relaxed font-medium">
                  Transform your static buyer list into a competitive marketplace. Our transparent leaderboard tracks performance metrics in real-time, incentivizing salvagers to bid higher and collect faster to maintain their top-tier status.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { icon: FiTrendingUp, title: "Higher Offer Yields", desc: "Buyers know they are competing for rank, naturally driving up the average offer price for your BER items to secure more claims." },
                  { icon: FiClock, title: "Faster Turnaround", desc: "Collection speeds are strictly tracked and factored into rankings, ensuring your warehouses don't become permanent storage facilities." },
                  { icon: FiStar, title: "Priority AI Routing", desc: "Top-ranked salvagers automatically receive priority from the AI routing engine for premium, high-value claims." }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 text-yellow-400">
                      <SafeIcon icon={item.icon} size={20} />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">{item.title}</h4>
                      <p className="text-brand-accent/70 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Visual Mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 lg:p-8 shadow-2xl">
                <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                  <h4 className="text-white font-bold text-lg">Top Salvagers (Q3)</h4>
                  <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold text-white flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span> Live
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Rank 1 */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between relative overflow-hidden group hover:bg-white/10 transition-colors">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-400"></div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center text-yellow-900 font-black shadow-lg">
                        1
                      </div>
                      <div>
                        <p className="text-white font-bold">TechRecycle ZA</p>
                        <p className="text-xs text-white/60">Win Rate: 94% • 1.2 Days Avg</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-yellow-400 font-black text-lg">R 2.4M</p>
                      <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Total Payout</p>
                    </div>
                  </div>

                  {/* Rank 2 */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between relative overflow-hidden group hover:bg-white/10 transition-colors">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-300"></div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-400 flex items-center justify-center text-gray-800 font-black shadow-lg">
                        2
                      </div>
                      <div>
                        <p className="text-white font-bold">Gauteng Salvage Co.</p>
                        <p className="text-xs text-white/60">Win Rate: 88% • 1.5 Days Avg</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-black text-lg">R 1.8M</p>
                      <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Total Payout</p>
                    </div>
                  </div>

                  {/* Rank 3 */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between relative overflow-hidden group hover:bg-white/10 transition-colors">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-600"></div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-black shadow-lg">
                        3
                      </div>
                      <div>
                        <p className="text-white font-bold">E-Waste Solutions</p>
                        <p className="text-xs text-white/60">Win Rate: 82% • 2.1 Days Avg</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-black text-lg">R 950k</p>
                      <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Total Payout</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating popover */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-6 lg:-right-12 -bottom-6 bg-white rounded-xl p-4 shadow-2xl border border-gray-100 flex items-center gap-3 z-20"
              >
                <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                  <SafeIcon icon={FiTrendingUp} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Performance Bonus</p>
                  <p className="font-black text-gray-900">+15% Allocation Rate</p>
                </div>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section id="security" className="py-24 bg-surface-alt border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-16 h-16 bg-white border border-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
            <SafeIcon icon={FiSecure} className="text-brand text-2xl" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">Enterprise-Grade Security</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-12">
            We understand that insurance data is highly sensitive. SalvageOS is built on a foundation of strict access controls, data encryption, and immutable audit logs.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-left">
            <div className="bg-white p-6 rounded-2xl border border-gray-100">
              <h4 className="font-bold text-gray-900 mb-2">Role-Based Access (RBAC)</h4>
              <p className="text-sm text-gray-500">Strict permission boundaries ensure salvagers only see claims explicitly assigned to them.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100">
              <h4 className="font-bold text-gray-900 mb-2">Data Isolation</h4>
              <p className="text-sm text-gray-500">Row-Level Security (RLS) guarantees that tenant data can never bleed across accounts.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100">
              <h4 className="font-bold text-gray-900 mb-2">Immutable Logs</h4>
              <p className="text-sm text-gray-500">Every status change, note, and financial transaction is permanently logged for compliance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-brand-dark text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">Ready to digitize your salvage?</h2>
          <p className="text-xl text-brand-accent/80 mb-10 font-medium">Join the platform that is recovering millions in salvage value and shrinking turnaround times by 80%.</p>
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-10 py-5 bg-white text-brand-dark rounded-xl font-black text-lg hover:bg-gray-100 transition-colors shadow-2xl shadow-black/50"
          >
            Access Portal <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
      </section>

      {/* Expanded Footer */}
      <footer className="bg-white pt-16 pb-8 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
                  <SafeIcon icon={FiBox} className="text-white text-xl" />
                </div>
                <span className="text-xl font-black text-gray-900 tracking-tight">SalvageOS</span>
              </div>
              <p className="text-sm text-gray-500 max-w-sm mb-6 leading-relaxed">
                The leading operating system for insurance salvage. We connect insurers with buyers to maximize recovery yields automatically.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Platform</h4>
              <ul className="space-y-3 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-brand transition-colors">Features</a></li>
                <li><a href="#workflow" className="hover:text-brand transition-colors">How it Works</a></li>
                <li><a href="#reports" className="hover:text-brand transition-colors">Reports</a></li>
                <li><a href="#leaderboard" className="hover:text-brand transition-colors">Performance Tracking</a></li>
                <li><a href="#security" className="hover:text-brand transition-colors">Security</a></li>
                <li><button onClick={() => navigate('/auth')} className="hover:text-brand transition-colors">Partner Portal</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-3 text-sm text-gray-500">
                <li><a href="#" className="hover:text-brand transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-brand transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-brand transition-colors">Cookie Policy</a></li>
                <li><a href="#" className="hover:text-brand transition-colors">Compliance</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} SalvageOS Operations. All rights reserved.</p>
            <div className="flex items-center gap-4 text-sm text-gray-400 font-medium">
              <span>System Status: <span className="text-green-500 font-bold">Operational</span></span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};